import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { toast, Toaster } from "sonner";
import { UserCog, Plus, Pencil, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { ddmmyyyy } from "../../lib/dates";

/**
 * Super-admin-only page (Mod 1).
 * Allows the super-admin to create / edit / delete other admin accounts (admin OR super_admin).
 * Hidden from regular admins; backend also enforces 403.
 */
export default function AdminAdmins() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "admin" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/admins");
            setList(data);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error("Super-admin only");
                navigate("/admin", { replace: true });
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (user && user.role !== "super_admin") {
            toast.error("Super-admin only");
            navigate("/admin", { replace: true });
            return;
        }
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: "", email: "", phone: "", password: "", role: "admin" });
        setOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            name: row.name || "",
            email: row.email || "",
            phone: row.phone || "",
            password: "",
            role: row.role || "admin",
        });
        setOpen(true);
    };

    // Iter 45.1 — normalise whatever the super-admin typed into strict
    // +91XXXXXXXXXX before sending. Accepts:
    //   "9876543210", "98765-43210", "+91 98765 43210", "919876543210", etc.
    // Returns null for empty input so the backend stores no phone field.
    const normalisePhone = (raw) => {
        const s = String(raw || "").replace(/[^\d+]/g, "");
        if (!s) return null;
        // Already +91 + 10 digits
        if (/^\+91\d{10}$/.test(s)) return s;
        // Missing +, has 91 prefix
        if (/^91\d{10}$/.test(s)) return "+" + s;
        // Bare 10 digits starting 6-9
        if (/^[6-9]\d{9}$/.test(s)) return "+91" + s;
        // Return as-is; backend will surface a clear 422 which we now render.
        return s;
    };

    // Iter 45.1 — FastAPI returns 422 as `detail: [{msg, loc, ...}, ...]`.
    // The previous "|| 'Save failed'" branch coerced the array to
    // "[object Object]" in the toast, hiding the real reason (usually a bad
    // phone format). This surfaces the first field message clearly.
    const humanErr = (err, fallback) => {
        const d = err?.response?.data?.detail;
        if (Array.isArray(d) && d.length) {
            const first = d[0];
            const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : "";
            return field ? `${field}: ${first.msg}` : first.msg;
        }
        return d || fallback;
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
        if (!editing && !form.password) { toast.error("Password is required for new admin"); return; }
        setSaving(true);
        try {
            const phone = normalisePhone(form.phone);
            if (editing) {
                const payload = { name: form.name, email: form.email, phone, role: form.role };
                if (form.password) payload.password = form.password;
                await api.put(`/admin/admins/${editing.id}`, payload);
                toast.success("Account updated");
            } else {
                await api.post(`/admin/admins`, {
                    name: form.name, email: form.email, phone,
                    password: form.password, role: form.role,
                });
                toast.success(`${form.role === "super_admin" ? "Super-admin" : "Staff"} created`);
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(humanErr(err, "Save failed"));
        } finally { setSaving(false); }
    };

    const remove = async (row) => {
        if (row.id === user.id) { toast.error("You cannot delete yourself"); return; }
        if (!window.confirm(`Delete ${row.email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/admins/${row.id}`);
            toast.success("Admin deleted");
            load();
        } catch (err) {
            toast.error(humanErr(err, "Delete failed"));
        }
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8 max-w-5xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <UserCog className="w-5 h-5 text-[#00FF66]" />
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Admins & Staff</h1>
                    </div>
                    <button onClick={openCreate} data-testid="add-admin-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> Add Admin / Staff
                    </button>
                </div>
                <p className="text-slate-400 text-sm mt-1">Manage super-admins and staff accounts. Both roles receive WhatsApp broadcasts for new bookings, payments, and cancellations. Visible only to super-admins.</p>

                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="admins-table">
                    {loading ? (
                        <div className="p-8 text-slate-500 text-sm text-center">Loading…</div>
                    ) : list.length === 0 ? (
                        <div className="p-8 text-slate-500 text-sm text-center">No admins yet.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-950 text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-800">
                                <tr>
                                    <th className="text-left px-4 py-3">Name</th>
                                    <th className="text-left px-4 py-3">Email</th>
                                    <th className="text-left px-4 py-3">Phone</th>
                                    <th className="text-left px-4 py-3">Role</th>
                                    <th className="text-left px-4 py-3">Created</th>
                                    <th className="text-right px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((row) => {
                                    const isSelf = row.id === user.id;
                                    const isSuper = row.role === "super_admin";
                                    return (
                                        <tr key={row.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`admin-row-${row.id}`}>
                                            <td className="px-4 py-3 text-white font-semibold">{row.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#00FF66]">you</span>}</td>
                                            <td className="px-4 py-3 text-slate-300">{row.email}</td>
                                            <td className="px-4 py-3 text-slate-400">{row.phone || "—"}</td>
                                            <td className="px-4 py-3">
                                                {isSuper ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] text-[10px] font-bold uppercase tracking-widest">
                                                        <ShieldCheck className="w-3 h-3" /> Super
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-bold uppercase tracking-widest">
                                                        <ShieldAlert className="w-3 h-3" /> Staff
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-500 text-xs">{ddmmyyyy(row.created_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button onClick={() => openEdit(row)} title="Edit"
                                                    data-testid={`edit-admin-${row.id}`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded text-slate-400 hover:text-white hover:bg-slate-800">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => remove(row)} disabled={isSelf} title={isSelf ? "Can't delete yourself" : "Delete"}
                                                    data-testid={`delete-admin-${row.id}`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded text-slate-400 hover:text-red-400 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={save} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4" data-testid="admin-form">
                        <h3 className="font-display text-2xl font-bold uppercase text-white">{editing ? "Edit Account" : "New Admin / Staff"}</h3>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Name</label>
                            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="admin-name-input"
                                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="admin-email-input"
                                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Phone (+91…)</label>
                            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+919999999999" data-testid="admin-phone-input"
                                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                                {editing ? "New Password (leave blank to keep)" : "Password"}
                            </label>
                            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                                placeholder={editing ? "Leave blank to keep current" : "Min 6 characters"} data-testid="admin-password-input"
                                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Role</label>
                            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="admin-role-select"
                                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]">
                                <option value="admin">Staff (receives WhatsApp broadcasts, no user management)</option>
                                <option value="super_admin">Super Admin (full access incl. API Keys & Admin Management)</option>
                            </select>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setOpen(false)} className="flex-1 h-11 bg-slate-800 border border-slate-700 text-white font-bold uppercase tracking-widest rounded hover:bg-slate-700">Cancel</button>
                            <button type="submit" disabled={saving} data-testid="save-admin-btn"
                                className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50">
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </AdminLayout>
    );
}
